/**
 * EmptyState — canonical empty state component for the Builder platform.
 *
 * Wave B — replaces the minimal shell that previously lived here.
 * Wave 80 (2026-05-24) — opt-in `illustrationKey` renders Ideogram-burn
 *   asset (768px JPG, isométrico DESIGN style) acima do título. Backward-
 *   compat: se não passar, mantém o `icon` antigo.
 *
 * API:
 *   illustrationKey  — chave do catálogo @/assets/builder-illustrations
 *                     (ex: 'projects', 'daily-logs', 'photos'). Quando
 *                     presente, sobrescreve `icon`.
 *   icon             — Lucide icon ou inline SVG, fallback
 *   title            — 4–8 words
 *   description      — 1 sentence, max 18 words
 *   primaryAction    — single main CTA (label + onClick + optional icon)
 *   secondaryActions — 0–3 ghost-style CTAs below primary
 *   example          — miniatura "quando preenchido fica assim"
 *   feedsInto        — "alimenta o Orçamento e a Curva ABC"
 *   className        — extra classes on root div
 *
 * Animation:
 *   Container: fade-in + translate-up 8px, 220ms ease-out cubic-bezier(0.23, 1, 0.32, 1)
 *   Buttons: :active scale(0.97) 160ms ease-out (via Button component + active:)
 */
import { type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/Button';
import {
  illustrations,
  type EmptyStateIllustrationKey,
} from '@/assets/builder-illustrations';
import { useT } from '@/hooks/useT';

export interface EmptyStateProps {
  /**
   * Chave do catálogo de ilustrações Builder (Wave 80). Quando presente,
   * renderiza a JPG isométrica no topo em vez do `icon`. Asset é servido
   * de `/illustrations/empty-states/es-*.jpg` (público static).
   */
  illustrationKey?: EmptyStateIllustrationKey;
  /** Lucide icon node or inline SVG illustration — optional */
  icon?: ReactNode;
  /** 4–8 words describing the module */
  title: string;
  /** 1 sentence, max 18 words, describing what this module solves */
  description?: string;
  /** Single primary CTA */
  primaryAction?: {
    label: string;
    onClick: () => void;
    icon?: ReactNode;
  };
  /**
   * @deprecated Use `primaryAction` instead. Kept for backward-compat with
   * existing callsites that pass a raw ReactNode as `action`.
   */
  action?: ReactNode;
  /** 0–3 secondary / ghost CTAs */
  secondaryActions?: Array<{
    label: string;
    onClick: () => void;
    icon?: ReactNode;
  }>;
  /** Miniatura "quando preenchido fica assim" — renders in a dashed box */
  example?: ReactNode;
  /** "alimenta o Orçamento e a Curva ABC" — shown below with italic styling */
  feedsInto?: string;
  /** Extra CSS classes applied to the root container */
  className?: string;
}

export function EmptyState({
  illustrationKey,
  icon,
  title,
  description,
  primaryAction,
  action,
  secondaryActions,
  example,
  feedsInto,
  className,
}: EmptyStateProps) {
  const { locale } = useT();
  const isEn = locale === 'en';
  const illustration = illustrationKey ? illustrations.empty[illustrationKey] : undefined;
  return (
    <div
      data-anim="empty-state"
      className={cn(
        'mx-auto max-w-md px-4 py-12 text-center',
        // fade-in + translate-up, 220ms, cubic-bezier(0.23, 1, 0.32, 1)
        'animate-fade-in-up',
        className,
      )}
    >
      {/* Wave 80 — ilustração Ideogram (prioridade sobre icon) */}
      {illustration && (
        <div className="mx-auto mb-4 h-32 w-32 overflow-hidden rounded-2xl bg-surface-2">
          <img
            src={illustration.src}
            alt={illustration.alt}
            width={128}
            height={128}
            loading="lazy"
            className="h-full w-full object-contain"
            data-testid="empty-state-illustration"
          />
        </div>
      )}

      {/* Icon fallback (apenas se não houver ilustração Wave 80) */}
      {!illustration && icon && (
        <div className="inline-flex rounded-2xl bg-surface-2 p-3 text-ink-muted" aria-hidden>
          {icon}
        </div>
      )}

      {/* Title */}
      <h3 className="mt-4 text-[18px] font-semibold leading-snug tracking-tight text-ink">
        {title}
      </h3>

      {/* Description */}
      <p className="mt-2 text-[13px] leading-relaxed text-ink-muted">
        {description}
      </p>

      {/* Primary action */}
      {primaryAction && (
        <div className="mt-6 w-full">
          <Button
            variant="primary"
            size="lg"
            fullWidth
            onClick={primaryAction.onClick}
            className="min-h-[44px] active:scale-[0.97] transition-transform duration-[160ms] ease-out"
          >
            {primaryAction.icon && (
              <span className="mr-1.5 inline-flex shrink-0" aria-hidden>
                {primaryAction.icon}
              </span>
            )}
            {primaryAction.label}
          </Button>
        </div>
      )}

      {/* Backward-compat: raw ReactNode action (deprecated) */}
      {!primaryAction && action && (
        <div className="mt-6">{action}</div>
      )}

      {/* Secondary actions */}
      {secondaryActions && secondaryActions.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          {secondaryActions.map((action, i) => (
            <button
              key={i}
              type="button"
              onClick={action.onClick}
              className={cn(
                'inline-flex min-h-[44px] items-center gap-1.5 rounded-xl px-4 py-2.5',
                'text-[13px] font-medium text-ink-muted',
                'transition-[color,background-color,transform] duration-160 ease-out',
                'hover:bg-surface-3 hover:text-ink',
                'active:scale-[0.97]',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400',
              )}
            >
              {action.icon && (
                <span className="inline-flex shrink-0" aria-hidden>
                  {action.icon}
                </span>
              )}
              {action.label}
            </button>
          ))}
        </div>
      )}

      {/* Example miniatura */}
      {example && (
        <div
          className="mt-6 rounded-xl border border-dashed border-line p-4 opacity-60"
          aria-hidden
        >
          {example}
        </div>
      )}

      {/* feedsInto */}
      {feedsInto && (
        <p className="mt-4 text-[11px] italic text-ink-soft">
          <span className="not-italic font-medium">{isEn ? 'Feeds into' : 'Alimenta'}</span>{' '}
          {feedsInto}
        </p>
      )}
    </div>
  );
}
